import React from "react";

import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";

import GridOnOutlinedIcon from "@mui/icons-material/GridOnOutlined";
import VisibilityIcon from "@mui/icons-material/Visibility";
import PrintIcon from "@mui/icons-material/Print";

import { displayList } from "@auto/widgets/ListComponent";
import { getLocale, rest, t } from "@auto/services";
import { getServiceUri } from "@auto/metadata";
import { canAll } from "@src/security/permissions";

import {
  VOTING_CENTER_REPORTS_FACETS,
  VotingCenterReportsFacetButton,
  buildVotingCenterReportBackendFilters,
  isDefaultVotingCenterReportFilters,
  normalizeVotingCenterReportFilters
} from "./VotingCenterReportsFacets";

import styles from "./VotingCenterReportsList.module.styl";

const DEFAULT_PAGE_SIZE = 50;

let initialRange = null;
let initialRangeUsesLeft = 0;

let currentExportState = {
  search: "",
  sort: [],
  filters: buildVotingCenterReportBackendFilters()
};

const readRangeFromUrl = () => {
  const params = new URLSearchParams(window.location.search);
  const offset = Number(params.get("offset"));
  const limit = Number(params.get("limit"));

  const safeLimit =
    Number.isFinite(limit) && limit > 0
      ? Math.floor(limit)
      : DEFAULT_PAGE_SIZE;

  const safeOffset =
    Number.isFinite(offset) && offset >= 0
      ? Math.floor(offset)
      : 0;

  return {
    offset: safeOffset,
    limit: safeLimit,
    page: Math.floor(safeOffset / safeLimit),
    pageSize: safeLimit,
    hasExplicitRange: params.has("offset") || params.has("limit")
  };
};

const writeRangeToUrl = ({ offset, limit }) => {
  const url = new URL(window.location.href);

  url.searchParams.set(
    "offset",
    String(offset)
  );

  url.searchParams.set(
    "limit",
    String(limit)
  );

  window.history.replaceState(
    {},
    "",
    url.toString()
  );
};

const getEffectiveQuery = (query) => {
  const search =
    String(query?.search || "").trim();

  const sort =
    Array.isArray(query?.sort)
      ? query.sort
      : [];

  const filters =
    normalizeVotingCenterReportFilters(
      query?.filters
    );

  const hasActiveFilters =
    !isDefaultVotingCenterReportFilters(
      filters
    );

  const page =
    Number.isFinite(query?.page)
      ? query.page
      : 0;

  const pageSize =
    Number.isFinite(query?.pageSize)
      ? query.pageSize
      : DEFAULT_PAGE_SIZE;

  if (
    initialRange &&
    initialRange.hasExplicitRange &&
    initialRangeUsesLeft > 0 &&
    !search &&
    !hasActiveFilters &&
    page === 0 &&
    pageSize === DEFAULT_PAGE_SIZE
  ) {
    initialRangeUsesLeft -= 1;

    return {
      page: initialRange.page,
      pageSize: initialRange.pageSize,
      offset: initialRange.offset,
      limit: initialRange.limit,
      search,
      sort,
      filters
    };
  }

  initialRangeUsesLeft = 0;

  return {
    page,
    pageSize,
    offset: page * pageSize,
    limit: pageSize,
    search,
    sort,
    filters
  };
};

const selectorsForSortItem = (sortItem) => {
  const column = sortItem?.column;

  if (Array.isArray(column?.sortSelectors)) {
    return column.sortSelectors;
  }

  if (column?.sortSelector) {
    return [column.sortSelector];
  }

  if (sortItem?.field) {
    return [sortItem.field];
  }

  return [];
};

const buildSortPayload = (sort) => {
  const out = [];

  for (const sortItem of sort || []) {
    const direction =
      String(
        sortItem?.direction || ""
      ).toLowerCase();

    if (
      direction !== "asc" &&
      direction !== "desc"
    ) {
      continue;
    }

    for (
      const selector
      of selectorsForSortItem(sortItem)
    ) {
      if (selector) {
        out.push({
          selector,
          direction
        });
      }
    }
  }

  return out;
};


const parseDownloadFilename = (contentDisposition) => {
  const value = String(contentDisposition || "");
  const match = value.match(/filename\*?=(?:UTF-8''|\")?([^\";]+)/i);

  if (!match?.[1]) {
    return "voting-center-statistics.xlsx";
  }

  try {
    return decodeURIComponent(match[1].trim());
  } catch {
    return match[1].trim();
  }
};

const VotingCenterExportButton = () => {
  const [exporting, setExporting] = React.useState(false);

  const handleExport = async () => {
    if (exporting) {
      return;
    }

    setExporting(true);

    try {
      const response = await fetch(
        getServiceUri()
          + "reporting/voting-center-reports/export",
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            search: currentExportState.search,
            sort: currentExportState.sort,
            filters: currentExportState.filters,
            locale: getLocale()
          })
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const blob = await response.blob();
      const filename = parseDownloadFilename(
        response.headers.get("Content-Disposition")
      );
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");

      anchor.href = url;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error(
        "Voting center statistics export failed",
        error
      );
      window.alert(t("reporting.exportFailed"));
    } finally {
      setExporting(false);
    }
  };

  return (
    <Tooltip
      title={exporting
        ? t("reporting.exportingExcel")
        : t("reporting.exportExcel")}
    >
      <span>
        <IconButton
          size="small"
          disabled={exporting}
          aria-label={t("reporting.exportExcel")}
          onClick={handleExport}
        >
          {exporting
            ? <CircularProgress size={18} />
            : <GridOnOutlinedIcon fontSize="small" />}
        </IconButton>
      </span>
    </Tooltip>
  );
};

const formatNumber = (value) =>
  Number(value || 0).toLocaleString();

const numberOrNull = (value) => {
  if (
    value === null ||
    value === undefined ||
    value === ""
  ) {
    return null;
  }

  const number = Number(value);

  return Number.isFinite(number)
    ? number
    : null;
};

const formatDateTime = (value) => {
  if (!value) {
    return "";
  }

  return String(value)
    .replace("T", " ")
    .slice(0, 19);
};

const locationLabel = (row) => {
  return [
    row?.sectionName,
    row?.communeName,
    row?.departmentName
  ]
    .filter(Boolean)
    .join(" · ");
};

const isDisabledCenter = (row) => {
  return row?.enabled === false || Number(row?.status) === 2;
};

const disabledCellClass = (row) => {
  return isDisabledCenter(row)
    ? styles.disabledCell
    : "";
};

const joinClasses = (...values) =>
  values.filter(Boolean).join(" ");

const UtilizationCell = ({ row }) => {
  const registeredVoters =
    numberOrNull(row?.registeredVoterCount) ?? 0;

  const capacity =
    numberOrNull(row?.capacity);

  const utilization =
    capacity !== null && capacity > 0
      ? (
          registeredVoters
          * 100
          / capacity
        )
      : null;

  const visual =
    utilization === null
      ? 0
      : Math.max(
          0,
          Math.min(
            100,
            utilization
          )
        );

  return (
    <div
      className={joinClasses(
        styles.utilizationCell,
        disabledCellClass(row)
      )}
    >
      <div className={styles.utilizationNumbers}>
        <span>
          {formatNumber(
            registeredVoters
          )}
        </span>

        <span className={styles.capacityDivider}>
          /
        </span>

        <span>
          {capacity !== null
            ? formatNumber(capacity)
            : "—"}
        </span>
      </div>

      <div className={styles.utilizationTrack}>
        <span
          className={styles.utilizationFill}
          style={{
            width: `${visual}%`
          }}
        />
      </div>

      <div className={styles.utilizationPercent}>
        {utilization === null
          ? "—"
          : `${utilization.toFixed(1)}%`}
      </div>
    </div>
  );
};

const votingCenterReportsProjection = {
  storageKey:
    "haiti.voting-center-reports",

  facets:
    VOTING_CENTER_REPORTS_FACETS,

  columns: [
    {
      title: "common.code",
      field: "code",
      sortSelector: "code",

      render: (row) => (
        <span
          className={joinClasses(
            styles.code,
            disabledCellClass(row)
          )}
        >
          {row.code || "—"}
        </span>
      )
    },

    {
      title: "voter.votingCenter",
      field: "name",
      sortSelector: "name",

      render: (row) => (
        <div
          className={joinClasses(
            styles.centerCell,
            disabledCellClass(row)
          )}
        >
          <div className={styles.centerName}>
            {row.name || t("reporting.unnamedCenter")}
            {isDisabledCenter(row) ? (
              <span className={styles.disabledBadge}>
                Disabled
              </span>
            ) : null}
          </div>

          {row.address ? (
            <div className={styles.centerMeta}>
              {row.address}
            </div>
          ) : null}
        </div>
      )
    },

    {
      title: "reporting.location",
      field: "communeName",

      sortSelectors: [
        "communeName",
        "sectionName"
      ],

      render: (row) => (
        <div
          className={joinClasses(
            styles.locationCell,
            disabledCellClass(row)
          )}
        >
          <div className={styles.locationPrimary}>
            {row.communeName || "—"}
          </div>

          <div className={styles.centerMeta}>
            {locationLabel(row)}
          </div>
        </div>
      )
    },

    {
      title: "geo.capacity",
      field: "capacity",
      sortSelector: "capacity",
      filtering: false,

      render: (row) => (
        <span
          className={joinClasses(
            styles.metricStrong,
            disabledCellClass(row)
          )}
        >
          {formatNumber(row.capacity)}
        </span>
      )
    },

    {
      title: "reporting.applicants",
      field: "uniqueApplicantCount",
      sortSelector: "uniqueApplicantCount",
      filtering: false,

      render: (row) => (
        <span
          className={joinClasses(
            styles.metricStrong,
            disabledCellClass(row)
          )}
        >
          {formatNumber(
            row.uniqueApplicantCount
          )}
        </span>
      )
    },

    {
      title: "reporting.utilization",
      field: "capacityUtilization",
      sortSelector: "capacityUtilization",
      filtering: false,

      render: (row) => (
        <UtilizationCell row={row} />
      )
    },

    {
      title: "reporting.operators",
      field: "activeOperatorCount",
      sortSelector: "activeOperatorCount",
      filtering: false,

      render: (row) => (
        <span
          className={joinClasses(
            styles.metric,
            disabledCellClass(row)
          )}
        >
          {formatNumber(
            row.activeOperatorCount
          )}
        </span>
      )
    },

    {
      title: "reporting.lastActivity",
      field: "lastSubmissionAt",
      sortSelector: "lastSubmissionAt",
      filtering: false,

      render: (row) => {
        const last =
          formatDateTime(
            row.lastSubmissionAt
          );

        const first =
          formatDateTime(
            row.firstSubmissionAt
          );

        if (!last) {
          return null;
        }

        return (
          <div
            className={joinClasses(
              styles.activityCell,
              disabledCellClass(row)
            )}
          >
            <div className={styles.lastActivity}>
              {last}
            </div>

            {first ? (
              <div className={styles.centerMeta}>
                {t("common.since")} {first}
              </div>
            ) : null}
          </div>
        );
      }
    },

    {
      title: "common.actions",
      field: "__actions",
      sorting: false,
      filtering: false
    }
  ],

  beforeNavigationControl: ({ facetController }) => (
    <div className={styles.navigationControls}>
      <VotingCenterReportsFacetButton
        controller={facetController}
      />
      <VotingCenterExportButton />
    </div>
  ),

  actions: (row, gui) => (
    <span
      className={joinClasses(
        styles.actionsCell,
        disabledCellClass(row)
      )}
    >
      <Tooltip title={t("reporting.viewCenterReport")}>
        <IconButton
          size="small"
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();

            if (row.gid) {
              gui.goTo(
                `/reporting/voting-centers/${row.gid}`
              );
            }
          }}
        >
          <VisibilityIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      {canAll(gui, ["SEARCH_VOTER", "COUNT_VOTER", "READ_PERSON"]) ? (
        <Tooltip title={t("voterRoll.preview")}>
          <IconButton
            size="small"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();

              if (row.gid) {
                gui.goTo(
                  `/reporting/voting-centers/${row.gid}/voter-roll-preview`
                );
              }
            }}
          >
            <PrintIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ) : null}
    </span>
  ),

  fetch: async (query) => {
    const range =
      getEffectiveQuery(query);

    writeRangeToUrl({
      offset: range.offset,
      limit: range.limit
    });

    const sort =
      buildSortPayload(range.sort);

    const filters =
      buildVotingCenterReportBackendFilters(
        range.filters
      );

    currentExportState = {
      search: range.search,
      sort,
      filters
    };

    const response =
      await rest.request(
        getServiceUri()
          + "reporting/voting-center-reports/search",
        "POST",
        {
          search: range.search,
          filters,
          limit: range.limit,
          offset: range.offset,

          ...(sort.length
            ? { sort }
            : {})
        }
      );

    return {
      data:
        Array.isArray(response?.items)
          ? response.items
          : [],

      totalCount:
        Number(response?.total || 0),

      page:
        range.page
    };
  }
};

export const displayVotingCenterReports = (
  gui
) => {
  initialRange =
    readRangeFromUrl();

  initialRangeUsesLeft =
    initialRange.hasExplicitRange
      ? 3
      : 0;

  return displayList(
    gui,
    votingCenterReportsProjection
  );
};

export default displayVotingCenterReports;